import { DestroyRef, Directive, DOCUMENT, ElementRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { LayoutFacade } from '@web/store/layout/layout.facade';

@Directive({ selector: '[webGridCellWidth]' })
export class GridCellWidthDirective {
    private readonly element: HTMLElement = inject(ElementRef).nativeElement;
    private readonly document = inject<Document>(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutFacade = inject(LayoutFacade);

    public readonly leftWidth = input.required<number>();
    public readonly rightWidth = input.required<number>();
    public readonly elementLeftId = input.required<string>();
    public readonly elementRightId = input.required<string>();

    public readonly changeWidthToLeftEvent = output<void>();
    public readonly changeWidthToRightEvent = output<void>();

    private readonly MIN_COL_WIDTH: number = 1;

    private isResizing = false;

    constructor() {
        this.initDrag();

        // The handle is dropped from the DOM whenever the cell count changes,
        // which can happen mid-gesture. Leaving the store flag on would keep the
        // editor controls hidden and the resize cursor pinned for good.
        this.destroyRef.onDestroy(() => this.stopResizing());
    }

    private initDrag(): void {
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        fromEvent<MouseEvent>(this.element, 'mousedown')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((dragStartEvent: MouseEvent) => {
                dragStartEvent.preventDefault();
                this.startResizing();

                let initialX = dragStartEvent.clientX;
                const colL = this.document.getElementById(this.elementLeftId()) as HTMLElement;
                const colR = this.document.getElementById(this.elementRightId()) as HTMLElement;

                fromEvent<MouseEvent>(this.document, 'mousemove')
                    .pipe(takeUntil(dragEnd$), takeUntilDestroyed(this.destroyRef))
                    .subscribe((dragEvent: MouseEvent) => {
                        dragEvent.preventDefault();

                        const diff = initialX - dragEvent.clientX;
                        // Cells are sized in grid units, so one unit is worth a
                        // different number of pixels on each side — the step is the
                        // average of the two, remeasured as the cells reflow.
                        const widthInPixels =
                            (colL.clientWidth / this.leftWidth() + colR.clientWidth / this.rightWidth()) / 2;

                        if (diff > widthInPixels) {
                            if (this.leftWidth() === this.MIN_COL_WIDTH) {
                                return;
                            }

                            this.changeWidthToLeftEvent.emit();
                            initialX -= widthInPixels;
                            return;
                        }

                        if (diff < -widthInPixels) {
                            if (this.rightWidth() === this.MIN_COL_WIDTH) {
                                return;
                            }

                            this.changeWidthToRightEvent.emit();
                            initialX += widthInPixels;
                        }
                    });

                dragEnd$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.stopResizing());
            });
    }

    private startResizing(): void {
        this.isResizing = true;
        this.layoutFacade.setColumnWidthChanging(true);
        this.element.classList.add('dragging');
    }

    private stopResizing(): void {
        if (!this.isResizing) {
            return;
        }

        this.isResizing = false;
        this.layoutFacade.setColumnWidthChanging(false);
        this.element.classList.remove('dragging');
    }
}
